/** First letter capital, remaining letters lowercase — e.g. fLuid → Fluid */
export const capitalizeInitialLetter = (value) => {
  if (value == null || value === "") return value;
  const str = String(value).trim();
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/** Normalize GST numbers — e.g. gsT-123 → GST-123 */
export const normalizeGstNumber = (value) => {
  if (value == null || value === "") return value;
  const str = String(value).trim();
  if (!str) return str;
  if (/^gst/i.test(str)) {
    return `GST${str.slice(3).toUpperCase()}`;
  }
  return str.toUpperCase();
};

const formatNameField = (value) => {
  if (value == null || value === "") return value;
  return capitalizeInitialLetter(String(value).trim());
};

/** Map known medicine types to canonical stored values; sentence-case others. */
const formatMedicineTypeField = (value) => {
  if (value == null || value === "") return value;
  const str = String(value).trim();
  const key = str.toLowerCase().replace(/[\s_-]+/g, "");
  const knownTypes = {
    homeopathy: "Homeopathy",
    allopathy: "Allopathy",
    fluid: "FLuids",
    fluids: "FLuids",
    ivfluids: "FLuids",
  };
  if (knownTypes[key]) return knownTypes[key];
  return capitalizeInitialLetter(str);
};

const formatUnitTypeField = (value) => formatNameField(value);

const KNOWN_PATH_PACKAGING_TYPES = {
  box: "Box",
  bottle: "Bottle",
  packet: "Packet",
  each: "Each",
};

const formatPathPackagingTypeField = (value) => {
  if (value == null || value === "") return value;
  const str = String(value).trim();
  const key = str.toLowerCase();
  if (KNOWN_PATH_PACKAGING_TYPES[key]) return KNOWN_PATH_PACKAGING_TYPES[key];
  return capitalizeInitialLetter(str);
};

const formatQtyUnitField = (value) => {
  if (value == null || value === "") return value;
  const key = String(value).trim().toLowerCase();
  if (key === "ml") return "ml";
  if (key === "gm" || key === "g") return "gm";
  if (key === "piece" || key === "pieces" || key === "pc") return "piece";
  return key;
};

const KNOWN_MEDICINE_DETAILS = {
  box: "Box",
  capsule: "Capsule",
  syrup: "Syrup",
  tablet: "Tablet",
  fluid: "Fluid",
};

const formatMedicineDetailsField = (value) => {
  if (value == null || value === "") return value;
  const str = String(value).trim();
  const key = str.toLowerCase();
  if (KNOWN_MEDICINE_DETAILS[key]) return KNOWN_MEDICINE_DETAILS[key];
  return capitalizeInitialLetter(str);
};

const applyGstField = (target, field) => {
  if (target[field] != null && target[field] !== "") {
    target[field] = normalizeGstNumber(target[field]);
  }
};

const applyNameField = (target, field) => {
  if (target[field] != null && target[field] !== "") {
    target[field] = formatNameField(target[field]);
  }
};

export const formatNameForDisplay = (value) => formatNameField(value);
export const formatMedicineTypeForDisplay = (value) => formatMedicineTypeField(value);
export const formatUnitTypeForDisplay = (value) => formatUnitTypeField(value);
export const formatMedicineDetailsForDisplay = (value) => formatMedicineDetailsField(value);
export const formatGstForDisplay = (value) => normalizeGstNumber(value);

export const formatMedicineCatalogFields = (data = {}) => {
  if (!data || typeof data !== "object") return data;
  const formatted = { ...data };

  ["name", "medicineName", "manufacturer", "supplier", "supplierName", "supplierAddress", "medicineType", "medicineDetails"].forEach(
    (field) => {
      if (formatted[field] == null || formatted[field] === "") return;
      if (field === "medicineType") {
        formatted[field] = formatMedicineTypeField(formatted[field]);
      } else if (field === "medicineDetails") {
        formatted[field] = formatMedicineDetailsField(formatted[field]);
      } else {
        formatted[field] = formatNameField(formatted[field]);
      }
    }
  );

  applyGstField(formatted, "supplierGstNumber");
  applyGstField(formatted, "gstNumber");

  return formatted;
};

export const formatPathItemFields = (data = {}) => {
  if (!data || typeof data !== "object") return data;
  const formatted = { ...data };

  ["name", "supplierName", "supplierAddress", "description"].forEach((field) => {
    if (formatted[field] == null || formatted[field] === "") return;
    formatted[field] = formatNameField(formatted[field]);
  });

  // unit_type = packaging (Box/Bottle/Packet/Each)
  if (formatted.unitType != null && formatted.unitType !== "") {
    formatted.unitType = formatPathPackagingTypeField(formatted.unitType);
  }
  if (formatted.unit_type != null && formatted.unit_type !== "") {
    formatted.unit_type = formatPathPackagingTypeField(formatted.unit_type);
  }

  // qty_unit = piece / ml / gm
  if (formatted.qtyUnit != null && formatted.qtyUnit !== "") {
    formatted.qtyUnit = formatQtyUnitField(formatted.qtyUnit);
  }
  if (formatted.qty_unit != null && formatted.qty_unit !== "") {
    formatted.qty_unit = formatQtyUnitField(formatted.qty_unit);
  }

  // Keep BOM numeric fields as floats (0.5, 1.2) — never coerce via parseInt
  ["requiredQuantity", "required_quantity", "cost", "costPerUnit", "cost_per_unit", "details"].forEach(
    (field) => {
      if (formatted[field] === "" || formatted[field] == null) return;
      const parsed = Number.parseFloat(String(formatted[field]).trim().replace(/,/g, ""));
      if (Number.isFinite(parsed)) {
        formatted[field] = parsed;
      }
    }
  );

  applyGstField(formatted, "supplierGstNumber");
  applyGstField(formatted, "gstNumber");

  return formatted;
};

export const formatSupplierFields = (data = {}) => {
  if (!data || typeof data !== "object") return data;
  const formatted = { ...data };

  applyNameField(formatted, "name");
  applyNameField(formatted, "supplierName");
  applyNameField(formatted, "address");
  applyNameField(formatted, "supplierAddress");
  applyGstField(formatted, "gstNumber");
  applyGstField(formatted, "supplierGstNumber");

  return formatted;
};

/** Stock purchase invoices — supplier fields only (do not touch patient/hospital names). */
export const formatStockInvoiceSupplierFields = (data = {}) => {
  if (!data || typeof data !== "object") return data;
  const formatted = { ...data };

  applyNameField(formatted, "supplierName");
  applyNameField(formatted, "supplierAddress");
  applyGstField(formatted, "supplierGstNumber");

  return formatted;
};

export const formatMedicineInventoryFields = (data = {}) =>
  formatMedicineCatalogFields(data);

export const formatPathInventoryFields = (data = {}) =>
  formatPathItemFields(data);

export const normalizeMedicineList = (items) =>
  Array.isArray(items) ? items.map(formatMedicineCatalogFields) : items;

export const normalizeMedicineItem = (item) =>
  item && typeof item === "object" && !Array.isArray(item)
    ? formatMedicineCatalogFields(item)
    : item;

export const normalizePathBomList = (items) =>
  Array.isArray(items) ? items.map(formatPathItemFields) : items;

export const normalizePathBomItem = (item) =>
  item && typeof item === "object" && !Array.isArray(item)
    ? formatPathItemFields(item)
    : item;

export const normalizeSupplierList = (items) =>
  Array.isArray(items) ? items.map(formatSupplierFields) : items;

export const normalizeSupplierItem = (item) =>
  item && typeof item === "object" && !Array.isArray(item)
    ? formatSupplierFields(item)
    : item;

export const normalizeMedicineInventoryList = (items) =>
  Array.isArray(items) ? items.map(formatMedicineInventoryFields) : items;

export const normalizeMedicineInventoryItem = (item) =>
  item && typeof item === "object" && !Array.isArray(item)
    ? formatMedicineInventoryFields(item)
    : item;

export const normalizePathInventoryList = (items) =>
  Array.isArray(items) ? items.map(formatPathInventoryFields) : items;

export const normalizePathInventoryItem = (item) =>
  item && typeof item === "object" && !Array.isArray(item)
    ? formatPathInventoryFields(item)
    : item;

export const normalizeStockInvoiceList = (items) =>
  Array.isArray(items) ? items.map(formatStockInvoiceSupplierFields) : items;

export const normalizeStockInvoiceItem = (item) =>
  item && typeof item === "object" && !Array.isArray(item)
    ? formatStockInvoiceSupplierFields(item)
    : item;

/** Format inventory API responses whether they return one record or a list. */
export const normalizeMedicineInventoryResponse = (data) =>
  Array.isArray(data) ? normalizeMedicineInventoryList(data) : normalizeMedicineInventoryItem(data);

export const normalizePathInventoryResponse = (data) =>
  Array.isArray(data) ? normalizePathInventoryList(data) : normalizePathInventoryItem(data);

export const normalizeInvoiceListResponse = (data) =>
  Array.isArray(data) ? normalizeStockInvoiceList(data) : normalizeStockInvoiceItem(data);

export const handleFormattedNameBlur = (event, { setFieldValue, handleBlur } = {}) => {
  const { name, value } = event.target;
  const normalized = formatNameField(value);
  if (name && normalized != null && normalized !== value) {
    setFieldValue(name, normalized);
  }
  handleBlur?.(event);
};

export const handleFormattedGstBlur = (event, { setFieldValue, handleBlur } = {}) => {
  const { name, value } = event.target;
  const normalized = normalizeGstNumber(value);
  if (name && normalized != null && normalized !== value) {
    setFieldValue(name, normalized);
  }
  handleBlur?.(event);
};
